import { Alert, DataList, FormGroup, InputGroup, Radio, Switch } from '@patternfly/react-core';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StoreState } from '../../../store/store';
import Section from '../Section';
import SectionItem from '../SectionItem';
import SectionHelpText from '../SectionHelpText';
import { imageContourGenerationActionSet, imageContourGenerationAttachToSet, imageContourGenerationEnabledSet, imageContourGenerationRoiPhysicalPropertiesEnabledSet, imageContourGenerationRoiPhysicalPropertiesDensityTypeSet, imageSelectors } from '../../../store/image/imageSlice';
import { ContourAttachSeries } from '../../../store/image/image-types';
import ContourModelSelect from './ContourModelSelect';
import { Model, PhysicalProperty } from '../../../store/global-types/customization-types';
import FormSelect from '../../../components/form-select';

import '../customization-form.css';


interface ContourGenerationFormProps {
    outputId: string,
    model: Model,
}

const ContourGenerationForm = (props: ContourGenerationFormProps) => {

    const { outputId, model } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const output = useSelector((state: StoreState) => imageSelectors.selectOutputById(state, outputId));
    const contourGeneration = useSelector((state: StoreState) => imageSelectors.selectContourGenerationForOutput(state, outputId));

    const handleEnableContourGenerationToggled = useCallback(() => {
        if (contourGeneration) { dispatch(imageContourGenerationEnabledSet({ id: contourGeneration.id, isEnabled: !contourGeneration.isEnabled })); }
    }, [dispatch, contourGeneration]);

    const handleActionChanged = useCallback((action: string) => {
        if (contourGeneration) { dispatch(imageContourGenerationActionSet({ id: contourGeneration.id, actionName: action })); }
    }, [dispatch, contourGeneration]);

    const handleAttachContoursToOutput = useCallback(() => {
        if (contourGeneration) { dispatch(imageContourGenerationAttachToSet({ id: contourGeneration.id, attachTo: ContourAttachSeries.Output })); }
    }, [dispatch, contourGeneration]);

    const handleAttachContoursToInput = useCallback(() => {
        if (contourGeneration) { dispatch(imageContourGenerationAttachToSet({ id: contourGeneration.id, attachTo: ContourAttachSeries.Input })); }
    }, [dispatch, contourGeneration]);

    const handlePhysicalPropertiesEnabledToggled = useCallback(() => {
        if (contourGeneration) { dispatch(imageContourGenerationRoiPhysicalPropertiesEnabledSet({ id: contourGeneration.id, isEnabled: !contourGeneration.roiPhysicalPropertiesEnabled })); }
    }, [dispatch, contourGeneration]);

    const handlePhysicalPropertiesDensityTypeChanged = useCallback((value: string) => {
        if (contourGeneration) { dispatch(imageContourGenerationRoiPhysicalPropertiesDensityTypeSet({ id: contourGeneration.id, densityType: value as PhysicalProperty })); }
    }, [dispatch, contourGeneration]);

    // TODO: add section-specific output validity checking here later on if we have
    // any relevant form validation for those parts -- currently we don't
    const outputIsValid = true;

    const warningAlertRef = useRef<HTMLDivElement>(null);

    // Scroll the physical property warning alert into view when it first appears
    useEffect(() => {
        if (contourGeneration?.roiPhysicalPropertiesEnabled && warningAlertRef.current) {
            warningAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [contourGeneration?.roiPhysicalPropertiesEnabled]);

    if (!output || !contourGeneration) {
        return null;
    }


    return (
        <div>
            <DataList aria-label={`Image+ contour generation customization`} isCompact className="customization-form">

                <Section
                    title={undefined}
                    isModified={false}
                    hasValidationError={!outputIsValid}
                    validationError={undefined}
                >

                    <SectionItem label={t('customizationPage.image.contourGenerationSection.enabled.label')}>
                        <FormGroup>
                            <InputGroup>
                                <span data-testid="contour-generation-enabled-switch">
                                    <Switch
                                        label={t('customizationPage.image.contourGenerationSection.enabled.on')}
                                        labelOff={t('customizationPage.image.contourGenerationSection.enabled.off')}
                                        isChecked={contourGeneration.isEnabled}
                                        onChange={handleEnableContourGenerationToggled}
                                    />
                                </span>
                            </InputGroup>
                        </FormGroup>
                    </SectionItem>

                    {contourGeneration.isEnabled && (
                        <>

                            <SectionItem label={t('customizationPage.image.contourGenerationSection.action.label')}>
                                <ContourModelSelect
                                    initialAction={contourGeneration.action || ''}
                                    onSelect={handleActionChanged}
                                    model={model}
                                />
                            </SectionItem>

                            <SectionItem label={t('customizationPage.image.contourGenerationSection.attachTo.label')}>
                                <FormGroup
                                    role="radiogroup"
                                    isStack
                                >
                                    <Radio
                                        name="image-contour-generation-attach-to"
                                        id="image-contour-generation-attach-to-output"
                                        label={t('customizationPage.image.contourGenerationSection.attachTo.output')}
                                        onChange={handleAttachContoursToOutput}
                                        isChecked={contourGeneration.attachTo === ContourAttachSeries.Output}
                                    />
                                    <span data-testid="contour-generation-attach-to-input">
                                        <Radio
                                            name="image-contour-generation-attach-to"
                                            id="image-contour-generation-attach-to-input"
                                            label={t('customizationPage.image.contourGenerationSection.attachTo.input')}
                                            onChange={handleAttachContoursToInput}
                                            isChecked={contourGeneration.attachTo === ContourAttachSeries.Input}
                                        />
                                    </span>

                                </FormGroup>
                            </SectionItem>

                            {contourGeneration.attachTo === ContourAttachSeries.Input && (
                                <>
                                    <SectionItem label={t('customizationPage.image.contourGenerationSection.physicalProperties.enabled.label')}>
                                        <FormGroup>
                                            <InputGroup>
                                                <span data-testid="contour-generation-physical-properties-switch">
                                                    <Switch
                                                        label={t('customizationPage.image.contourGenerationSection.physicalProperties.enabled.on')}
                                                        labelOff={t('customizationPage.image.contourGenerationSection.physicalProperties.enabled.off')}
                                                        isChecked={contourGeneration.roiPhysicalPropertiesEnabled}
                                                        onChange={handlePhysicalPropertiesEnabledToggled}
                                                    />
                                                </span>
                                            </InputGroup>
                                        </FormGroup>
                                    </SectionItem>

                                    {contourGeneration.roiPhysicalPropertiesEnabled && (
                                        <>
                                            <SectionHelpText helpText={t('customizationPage.image.contourGenerationSection.physicalProperties.helpText')} />

                                            <SectionItem label={t('customizationPage.image.contourGenerationSection.physicalProperties.densityType.label')}>
                                                <FormSelect
                                                    selectedValue={contourGeneration.roiPhysicalPropertiesDensityType}
                                                    onSelect={handlePhysicalPropertiesDensityTypeChanged}
                                                    cssWidth="350px"
                                                    testId="image-contour-generation-density-type-select"
                                                    items={[
                                                        { value: PhysicalProperty.RelElecDensity, label: t('customizationPage.image.contourGenerationSection.physicalProperties.densityType.relElecDensity') },
                                                        { value: PhysicalProperty.RelMassDensity, label: t('customizationPage.image.contourGenerationSection.physicalProperties.densityType.relMassDensity') },
                                                    ]}
                                                />
                                            </SectionItem>

                                            {/* Safety mitigation SC-IMA-19 — warning text for physical property assignment */}
                                            <div data-testid="contour-generation-physical-properties-warning" ref={warningAlertRef}>
                                                <Alert variant="warning" isInline className="selectable" title={t('customizationPage.image.contourGenerationSection.physicalProperties.warning.title')}>
                                                    <p>{t('customizationPage.image.contourGenerationSection.physicalProperties.warning.body')}</p>
                                                </Alert>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                        </>
                    )}

                </Section>

            </DataList >
        </div>
    );
}

export default ContourGenerationForm;
